<script setup lang="ts">
import ViewLayout from '@/views/layouts/ViewLayout.vue'
import type { TabsPaneContext } from 'element-plus'
import { rangeToISOInTimezone } from '@/util/datetime'
import { DateTime } from 'luxon'
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import RlTimezoneSelect from '@/components/common/RlTimezoneSelect.vue'

const router = useRouter()
const activeTab = computed(() => router.currentRoute.value.name?.toString())
const previousRoute = ref()

const monthStartDateTime = DateTime.now().startOf('month')
const monthEndDateTime = DateTime.now().startOf('month').plus({ month: 1 })

const dateRangeFilter = ref<[Date, Date]>([monthStartDateTime.toJSDate(), monthEndDateTime.toJSDate()])
const timezoneFilter = ref(DateTime.now().zoneName)

const dateRangeISO = computed(() => rangeToISOInTimezone(dateRangeFilter.value, timezoneFilter.value))

const resetDateRange = (newRange: Array<Date> | null) => {
  if (!newRange) {
    dateRangeFilter.value = [monthStartDateTime.toJSDate(), monthEndDateTime.toJSDate()]
    return
  }
}

const handleTabClick = (tab: TabsPaneContext) => {
  const { startISO, endISO } = dateRangeISO.value
  router.push({
    name: tab.props.name!.toString(),
    query: {
      start_date: startISO,
      end_date: endISO,
    },
  })
}

// Store the previous route when component is mounted
onMounted(() => {
  // Get the previous route from router's history
  // Adjusted to type safety with TypeScript
  previousRoute.value = router.options.history.state.back
})
</script>

<template>
  <ViewLayout title="Infrastructure Downtime Summary">
    <template #controls>
      <div class="flex items-center gap-2 flex-nowrap">
        <el-date-picker
          v-model="dateRangeFilter"
          type="datetimerange"
          range-separator="to"
          start-placeholder="Start date"
          end-placeholder="End date"
          format="YYYY-MM-DD HH:mm:ss"
          date-format="YYYY/MM/DD ddd"
          time-format="A hh:mm:ss"
          editable
          :clearable="false"
          :shortcuts="[
            {
              text: 'Current Month',
              value: () => {
                const now = new Date()
                const start = new Date(now.getFullYear(), now.getMonth(), 1)
                const end = new Date(now.getFullYear(), now.getMonth() + 1, 1)
                return [start, end]
              },
            },
            {
              text: 'Last Month',
              value: () => {
                const now = new Date()
                const start = new Date(now.getFullYear(), now.getMonth() - 1, 1)
                const end = new Date(now.getFullYear(), now.getMonth(), 1)
                return [start, end]
              },
            },
          ]"
          @change="resetDateRange"
        />
        <RlTimezoneSelect class="w-56" v-model="timezoneFilter" />
      </div>
    </template>
    <template #navigation>
      <el-tabs v-model="activeTab" @tab-click="handleTabClick">
        <el-tab-pane label="Site" name="eos-downtime-sites-downtime-summary" />
        <el-tab-pane label="Structures" name="eos-downtime-structures-downtime-summary" />
        <el-tab-pane label="Racks" name="eos-downtime-racks-downtime-summary" />
        <el-tab-pane label="Miners" name="eos-downtime-miners-downtime-summary" />
      </el-tabs>
    </template>
    <div class="flex-1 flex flex-col gap-6">
      <router-view :start-date="dateRangeISO.startISO" :end-date="dateRangeISO.endISO" />
    </div>
  </ViewLayout>
</template>

<style scoped lang="scss">
@use '@/styles/element/overrides';

:deep() {
  @include overrides.custom-el-tabs;
}
</style>
